/* eslint-disable no-param-reassign */
import displayError from 'utils/displayError';
import { $detail, $form, $view } from 'signals/global.signals';
import * as dealsApi from 'api/deals.api';
import displayNotification from 'utils/displayNotification';
import getSelectValue from 'utils/getSelectValue';
import { fetchAndSetDealData } from 'components/views/FounderDeal/_helpers/founderDeal.resolvers';
import { createInvestorOnFounder, deleteInvestorOnFounder, updateInvestorOnFounder } from 'api/founderPipeline.api';
import { getBaseTodos } from 'constants';
import { handleHideModal } from 'components/global/_helpers/global.helpers';

export const handleSubmitEditDeal = async () => {
  $view.update({
    isModalLoading: true,
  });

  // Create a clean copy of the form data
  const formData = {
    name: $form.value.name,
    investmentStage: getSelectValue($form.value.investmentStage),
    typeOfRaise: getSelectValue($form.value.investmentStage) === 'Acquisition' ? 'Acquisition' : getSelectValue($form.value.typeOfRaise),
    currentValue: $form.value.currentValue,
    currentRaiseSize: $form.value.currentRaiseSize,
    amountCommitted: $form.value.amountCommitted,
    minimumInvestment: $form.value.minimumInvestment,
    fundingDate: $form.value.fundingDate,
    usesOfCapital: $form.value.usesOfCapital,
    preMoneyValuation: $form.value.preMoneyValuation,
    amountPreviouslyRaised: $form.value.amountPreviouslyRaised,
    valuationLastRound: $form.value.valuationLastRound,
    cap: $form.value.cap,
    discountPercent: $form.value.discountPercent,
    interestRate: $form.value.interestRate,
    dealTerms: $form.value.dealTerms,
  };

  try {
    await dealsApi.updateDeal($form.value.id, formData);
    displayNotification({ message: 'Deal updated successfully', title: 'Deal Updated' });
    await fetchAndSetDealData($form.value.id);
  } catch (error) {
    displayError({
      message: 'Failed to update deal. Please try again.',
      title: 'Update Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
  }
};

export const handleSubmitAddInvestor = async () => {
  const { newInvestor } = $form.value;
  if (!newInvestor.name || !newInvestor.amount) {
    displayError({
      title: 'Missing Information',
      message: 'Please fill in both the name and amount fields.',
    });
    return;
  }

  try {
    const { existingInvestors, id } = $view.value;
    const updatedInvestors = [...existingInvestors, newInvestor];
    await dealsApi.updateDealExistingInvestors(id, updatedInvestors);
    $form.update({
      newInvestor: { name: undefined, amount: undefined },
    });
    $view.update({ isModalVisible: false });
    displayNotification({
      title: 'Investor Added',
      message: 'The investor has been added to the deal.',
    });
  } catch (error) {
    displayError({
      message: 'Failed to add investor to deal. Please try again.',
      title: 'Add Investor Error',
    });
  }
};

export const handleSubmitRemoveInvestor = async () => {
  $view.update({
    isModalLoading: true,
  });
  try {
    await deleteInvestorOnFounder($form.value?.id);
    // eslint-disable-next-line no-use-before-define
    handleHideModal();
    await fetchAndSetDealData($detail.value.id);
  } catch (error) {
    displayError({
      message: 'Failed to remove investor from deal. Please try again.',
      title: 'Remove Investor Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
  }
};

export const handleSubmitAddInvestorToDeal = async () => {
  $view.update({
    isModalLoading: true,
  });
  try {
    const dealId = $form.value.id; // We need to persist becuase it will get lost when we hide the modal
    const [recordType, investorId] = getSelectValue($form.value?.investorId).split('-');

    const formData = {
      dealId: Number($form.value.id),
      pipelineStatus: getSelectValue($form.value?.pipelineStatus),
      previouslyCommitted: Number($form.value?.previouslyCommitted),
      amountInvested: Number($form.value?.amountInvested),
      investorId: Number(investorId),
      recordType,
      todos: getBaseTodos(),
    };

    await createInvestorOnFounder(formData);
    displayNotification({
      message: 'Investor added to deal successfully',
      type: 'success',
    });
    // eslint-disable-next-line no-use-before-define
    handleHideModal();
    await fetchAndSetDealData(dealId);
  } catch (error) {
    if (error.message === 'Investor already on pipeline') {
      displayNotification({
        message: 'Investor already in pipeline for this deal. Cannot add again.',
        variant: 'warning',
        title: 'Validation Error!',
      });
      return;
    }
    displayError({
      message: 'Failed to add investor to deal. Please try again.',
      title: 'Add Investor Error',
    });
  } finally {
    $view.update({ isModalLoading: false });
  }
};

export const handleSubmitEditPipelineItem = async () => {
  $view.update({
    isModalLoading: true,
  });
  try {
    await updateInvestorOnFounder($form.value?.pipelineId, {
      amountInvested: Number($form.value?.amountInvested),
      previouslyCommitted: Number($form.value?.previouslyCommitted),
    });
    handleHideModal();
    await fetchAndSetDealData($detail.value.id);
  } catch (error) {
    displayError({
      message: 'Failed to update investor information. Please try again.',
      title: 'Update Error',
    });
  } finally {
    $view.update({ isModalLoading: false });
  }
};
